import { useEffect, useState } from "react";
import CompanySetupLayout from "../../../components/layouts/CompanySetupLayout";
import { APP_CONFIG, moneyFormat } from "../../../helpers/appHelpers";
import { IoChevronBackOutline } from "react-icons/io5";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import Label from "../../../components/form_fields/Label";
import OrangeButton from "../../../components/buttons/OrangeButton";
import { DetailedSubscriptionTierDto, SubscriptionTierOrderBy } from "../../../models/subscription-tier.dto";
import { useGetSubscriptionTierPackages } from "../../../services/subscription-tier.service";
import { PATHS } from "../../../routes/routes.paths";
import { PERMISSIONS, useValidateUserPermissions } from "../../../helpers/usePermissionManager";
import { useGetPaymentInstitution } from "../../../services/payment-institution.service";
import { PaymentInstitutionOrderBy, PAYMENT_CHANNEL_TYPE } from "../../../models/payment-institution.dto";
import useUserStore from "../../../state-management/useUserStore";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import { InvoicePaymentDto } from "../../../models/invoice.dto";
import {
	useInitiatePayment,
	useWatchInvoiceByReference,
} from "../../../services/invoice.service";
import { useRefreshOwnOrganizationConfig } from "../../../services/organization-config.service";
import SkewLoader from "react-spinners/SkewLoader";

type StartedPayment = {
	checkoutUrl?: string;
	msisdn?: string;
};

const SubscriptionPayment = () => {
	const hasPermission = useValidateUserPermissions(
		[PERMISSIONS.SUBSCRIPTIONS_WRITE], 
		"OR",
		PATHS.OVERVIEW
	);
	const navigate = useNavigate();
	const { currentUser } = useUserStore();
	const userOrganization = currentUser.user.organization;
	const [searchParams, setSearchParams] = useSearchParams();
	const reference = searchParams.get("reference");
	const subscriptionTierId = searchParams.get("subscriptionTierId");
	const subscriptionStatus = searchParams.get("subscriptionStatus");
	const [subscriptionTier, setSubscriptionTier] = 
		useState<DetailedSubscriptionTierDto | undefined>(undefined);

	const {
		register,
		handleSubmit,
		control,
		watch,
		setValue,
		formState: { errors },
	} = useForm<InvoicePaymentDto>({});
		
	const { 
		data: subscriptionTierPackages, 
		isLoading: isLoadingSubscriptions
	} = useGetSubscriptionTierPackages({
		detailed: true,
		page: 1,
		limit: 20,
		orderBy: SubscriptionTierOrderBy.PRICE_AMOUNT_ASC,
	});
	
	const channelCode = watch("channelCode");
	const isCardPayment = channelCode === "CARD";

	// Card payments pick from bank institutions, mobile money from wallets
	const { data: paymentInstitutions } = useGetPaymentInstitution({
		active: true,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: PaymentInstitutionOrderBy.NAME_ASC,
		paymentChannelType: isCardPayment
			? PAYMENT_CHANNEL_TYPE.BANK
			: PAYMENT_CHANNEL_TYPE.WALLET,
		countryId: userOrganization!.countryId,
	});

	// The selected network belongs to the previous channel's institution list
	useEffect(() => {
		setValue("network", "");
	}, [channelCode]);

	useEffect(() => {
		if (isLoadingSubscriptions || !subscriptionTierId) return;

		setSubscriptionTier(
			subscriptionTierPackages?.data.find(
				subscriptionTier => subscriptionTier.id === subscriptionTierId
			)
		);
	}, [subscriptionTierId, isLoadingSubscriptions])
	
	const [uploading, setUploading] = useState(false);
	const [startedPayment, setStartedPayment] = useState<StartedPayment | null>(null);
	const { mutate: initiatePayment } = useInitiatePayment();
	const refreshOrganizationConfig = useRefreshOwnOrganizationConfig();

	// Once payment has started, keep checking the invoice until it's paid or cancelled
	const { invoice } = useWatchInvoiceByReference(
		reference,
		!!startedPayment && !subscriptionStatus,
	);

	const showOutcome = (status: "SUCCESS" | "FAILED") => {
		const params: Record<string, string> = { subscriptionStatus: status };
		if (subscriptionTierId) params.subscriptionTierId = subscriptionTierId;
		if (reference) params.reference = reference;
		setSearchParams(params, { replace: true });
	};

	useEffect(() => {
		if (!startedPayment || subscriptionStatus || !invoice) return;
		if (invoice.status === "PAID") showOutcome("SUCCESS");
		if (invoice.status === "CANCELLED" || invoice.status === "FAILED") showOutcome("FAILED");
	}, [invoice?.status, startedPayment]);

	// The new package's limits (e.g. how many companies can be created) apply as soon
	// as payment succeeds, whether we detected it here or checkout redirected back
	useEffect(() => {
		if (subscriptionStatus === "SUCCESS") refreshOrganizationConfig();
	}, [subscriptionStatus]);

	const retryPayment = () => {
		setStartedPayment(null);
		const params: Record<string, string> = {};
		if (subscriptionTierId) params.subscriptionTierId = subscriptionTierId;
		if (reference) params.reference = reference;
		setSearchParams(params, { replace: true });
	};

	const onSubmit: SubmitHandler<InvoicePaymentDto> = (data) => {
		if (!reference) return;
		setUploading(true);

		initiatePayment(
			{
				payload: {
					...data,
					reference,
				},
			},
			{
				onSuccess: (response) => {
					const checkoutUrl = response.data.checkoutUrl;
					setStartedPayment({ checkoutUrl, msisdn: data.msisdn });

					// Browsers may block this because it follows a network request, so
					// the waiting screen also offers a button to open checkout
					if (checkoutUrl) window.open(checkoutUrl, "_blank", "noopener");
				},
				onSettled: () => setUploading(false),
			}
		);
	};

	return (
		<CompanySetupLayout pathname={window.location.pathname}>
			<Helmet>
				<title>Subscription Payment | Akatua</title>
			</Helmet>
			{hasPermission && (
				<>
				{!subscriptionStatus && (
					<div>
						{/* Back button */}
						<div
							onClick={() => {
								navigate(PATHS.CREATE_PAYROLL_COMPANY + "?displayPackages=true");
							}}
							className="flex justify-center w-full mb-12 cursor-pointer"
						>
							<div className="flex text-primary-blue w-[1160px] items-center text-2sm space-x-2">
								<IoChevronBackOutline className="text-sm" />
								<p>Go Back</p>
							</div>
						</div>
						<div className="flex justify-center items-start space-x-6">
							{!reference ? (
								<div className="w-[560px] bg-[#26A0F91A] rounded-lg p-8 text-center text-sm">
									<h1 className="font-bold text-xl">No invoice to pay</h1>
									<p className="mt-3 text-charcoal">
										Choose a package first. You&apos;ll be brought back here to pay for it.
									</p>
									<OrangeButton
										title="Choose a package"
										onClick={() =>
											navigate(PATHS.CREATE_PAYROLL_COMPANY + "?displayPackages=true")
										}
										className="px-10 mt-8"
									/>
								</div>
							) : startedPayment ? (
								<div className="w-[560px] bg-[#26A0F91A] rounded-lg p-8 text-center text-sm">
									<div className="flex justify-center">
										<SkewLoader color="#F58B00" />
									</div>
									<h1 className="font-bold text-xl mt-6">Waiting for your payment</h1>
									<p className="mt-3 text-charcoal">
										{startedPayment.checkoutUrl
											? "Complete the payment on the checkout page that opened in a new tab. This page will update automatically once it goes through."
											: `Approve the payment prompt sent to ${
													startedPayment.msisdn ? `+${startedPayment.msisdn}` : "your phone"
											  }. This page will update automatically once it goes through.`}
									</p>
									<div className="flex justify-center gap-3 mt-8">
										{startedPayment.checkoutUrl && (
											<OrangeButton
												title="Open checkout"
												onClick={() =>
													window.open(startedPayment.checkoutUrl, "_blank", "noopener")
												}
												className="px-8"
											/>
										)}
										<button
											type="button"
											onClick={() => setStartedPayment(null)}
											className="py-4 px-8 rounded-lg border border-gray-300 text-2xs text-gray-600 bg-white"
										>
											Use a different payment method
										</button>
									</div>
								</div>
							) : (
							/* Subscription form */
							<form 
								onSubmit={handleSubmit(onSubmit)}
								className="w-[560px] bg-[#26A0F91A] rounded-lg p-8"
							>
								<h3 className="text-2xs">
									{subscriptionTier ? "Subscribe to" : "Pay outstanding invoice"}
								</h3>
								<h1 className="font-bold text-2xl mb-8">
									{subscriptionTier?.name || invoice?.description}
								</h1>
								<div className="space-y-6">
									<div className="flex w-full justify-between items-center">
										<div className="space-y-1">
											<h3 className="font-bold text-base">Subscription</h3>
											<p className="text-2xs text-charcoal">
														{getBillingLabel(subscriptionTier)}
													</p>
										</div>
										<p className="text-primary-blue text-lg font-bold">
											{/* Opened for an outstanding invoice, there's no package to price from */}
											{!subscriptionTier
												? invoice
													? moneyFormat(String(invoice.amount))
													: ""
												: Number(subscriptionTier.priceAmount) === 0
													? "FREE"
													: getPackageBillingStats(subscriptionTier)
											}
										</p>
									</div>
									<div className="w-full space-y-2">
										<h3 className="font-bold text-base">Payment Type</h3>
										<div>
											<div className="flex items-center gap-7 accent-[#0b49761a]">
												<div className="flex items-center gap-3">
													<input 
														type="radio" 
														id="channelCodeCard" 
														{...register("channelCode", {
															required: "Select payment type here",
														})}
														value="CARD"
														onInput={() => setValue("channelCode", "CARD")}
													/>
													<label htmlFor="channelCodeCard">Card</label>
												</div>
												<div className="flex items-center gap-3">
													<input 
														type="radio" 
														id="channelCodeWallet" 
														{...register("channelCode", {
															required: "Select payment type here",
														})}
														value="MOBILE_MONEY"
													/>
													<label htmlFor="channelCodeWallet">Mobile Money</label>
												</div>
											</div>
											{errors.channelCode && (
												<p className="text-red-500 text-sm mt-1">
													{errors.channelCode.message}
												</p>
											)}
										</div>
									</div>
									{channelCode && (
										<div className="w-full space-y-2">
											<h3 className="font-bold text-base">
												{isCardPayment ? "Card issuing bank" : "Wallet"}
											</h3>
											<div>
												<select
													className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
														errors.network
															? " border border-red-500 focus:border-red-500 focus:outline-red-500"
															: "border-none"
													}`}
													{...register("network", {
														required: isCardPayment
															? "Select the bank that issued your card"
															: "Select wallet here",
													})}
												>
													<option value="">
														{isCardPayment ? "Select the bank that issued your card" : "Select Wallet"}
													</option>
													{paymentInstitutions?.data?.map((item) => (
														<option key={item.id} value={item.shortName}>
															{item.name}
														</option>
													))}
												</select>
												{errors.network && (
													<p className="text-red-500 text-sm mt-1">
														{errors.network.message}
													</p>
												)}
												{isCardPayment && (
													<p className="text-xs text-charcoal mt-1">
														You&apos;ll enter your card details on our payment partner&apos;s secure checkout page after you click Subscribe.
													</p>
												)}
											</div>
										</div>
									)}
									<div className="w-full space-y-2">
										<h3 className="font-bold text-base">Telephone</h3>
										<div>
											<Controller
												name={"msisdn"}
												control={control}
												rules={{
													required:
														channelCode === "MOBILE_MONEY"
															? "Enter the mobile money number to charge"
															: false,
													maxLength: {
														value: 12,
														message: "Enter a valid phone number",
													},
												}}
												render={({ field }) => (
													<div
														className={`w-full bg-secondary-gray rounded-lg mt-2 flex items-center ${
															errors.msisdn
																? "border border-red-500 focus:border-red-500 focus:outline-red-500"
																: "border-none"
														}`}
													>
														<PhoneInput
															{...field}
															country={"gh"}
															inputStyle={{
																height: errors.msisdn ? "50px" : "48px",
																border: "none",
																backgroundColor: "#F2F2F4",
																color: "black",
																boxShadow: "none",
																width: "95%",
																margin: "0px 0px 0px 15px",
																borderRadius: "10px",
															}}
															containerStyle={{
																// marginTop: "8px",
																height: errors.msisdn ? "50px" : "48px",
																border: "none",
																backgroundColor: "#F2F2F4",
																boxShadow: "none",
																borderRadius: "10px",
															}}
															buttonStyle={{
																border: "none",
																backgroundColor: "#F2F2F4",
																boxShadow: "none",
																padding: "0px 10px",
															}}
														/>
													</div>
												)}
											/>
											{errors.msisdn && (
												<p className="text-red-500 text-sm mt-1">
													{errors.msisdn.message}
												</p>
											)}
										</div>
									</div>
									<div className="border-secondary-darkgray h-[1px] border-b w-full" />
								</div>
								<div className="flex justify-center mt-14">
									<OrangeButton
										type="submit"
										title={"Subscribe"}
										className={"px-14"}
										disabled={uploading}
									/>
								</div>
							</form>
							)}
						</div>
					</div>
				)}
				{subscriptionStatus === "SUCCESS" && (
					<div className="mt-40 flex flex-col justify-center items-center text-sm">
						<div className="text-primary-blue bg-green-50 rounded-full flex justify-center items-center h-20 w-20 text-4xl">
							💫
						</div>
						<h1 className="text-2xl font-bold mt-10">
							Thank you for Subscribing!
						</h1>
						<p className="w-[458px] text-center mt-4">
							You have successfully subscribed to the{" "}
							<span className="font-medium">{subscriptionTier?.name} Package</span>. Start
							creating your companies
						</p>
						<div className="w-[15%] mt-8">
							<OrangeButton
								title={"Continue"}
								onClick={() => navigate(PATHS.COMPANY_ACCOUNT_SETUP)}
								className={"w-full"}
							/>
						</div>
					</div>
				)}
				{subscriptionStatus === "FAILED" && (
					<div className="mt-48 flex flex-col justify-center items-center text-sm">
						<div className="text-primary-blue bg-red-50 rounded-full flex justify-center items-center h-20 w-20 text-5xl">
							❗
						</div>
						<h1 className="text-2xl font-bold mt-10">Something went wrong!</h1>
						<p className="w-[400px] text-center mt-4">
							Your payment didn&apos;t go through, so your package hasn&apos;t changed.
							You can try again, choose another package, or contact support.
						</p>
						<div className="flex gap-3 mt-8">
							{reference && (
								<OrangeButton title="Try again" onClick={retryPayment} className="px-10" />
							)}
							<button
								type="button"
								onClick={() =>
									navigate(PATHS.CREATE_PAYROLL_COMPANY + "?displayPackages=true")
								}
								className="py-4 px-8 rounded-lg border border-gray-300 text-2xs text-gray-600"
							>
								Choose another package
							</button>
						</div>
					</div>
				)}
				</>
			)}
		</CompanySetupLayout>
	);
};

export default SubscriptionPayment;

const getBillingLabel = (subscriptionTierPackage?: DetailedSubscriptionTierDto) => {
	switch (subscriptionTierPackage?.billingPeriod) {
		case "MONTHLY":
			return "Billed monthly";
		case "ANNUALLY":
			return "Billed annually";
		default:
			return "";
	}
};

const getPackageBillingStats = (subscriptionTierPackage?: DetailedSubscriptionTierDto) => {
	const currencyCode = subscriptionTierPackage?.currency?.code || "$";
	const priceAmount = moneyFormat(subscriptionTierPackage?.priceAmount || "");

	switch(subscriptionTierPackage?.billingPeriod) {
		case "MONTHLY":
			return currencyCode + " " + priceAmount + "/mo"
		case "ANNUALLY":
			return currencyCode + " " + priceAmount + "/yr"
		default:
			return currencyCode + " " + priceAmount
	}
};
